import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, fallsAndRebounds } from '../skillUsage';

export const massAndWeightSkill: CodeoSkill = {
    name: SKILL_NAMES.massAndWeight,
    kind: 'codeo',
    description:
        'Tell how heavy a dropped object is through timing alone — how fast it falls, how high and ' +
        'how often it rebounds, how soon it is still — so a safe lands like a safe and a balloon ' +
        'like a balloon, at the same size.',
    checkLooksFor:
        'a fall on y that eases in (an .in ease) and rebounds that ease out (an .out ease), repeated over several hops',
    documentUsesSkill: check((script) => fallsAndRebounds(script)),
    instructions: `# Mass and Weight

Weight is not size. Three identical objects dropped the same distance read as light, medium or heavy purely from how they move after they let go. **The heavier it is, the faster it falls, the less it rebounds, the sooner it is still** — and the heaviest settles very slightly into the surface. Nothing about the object changes: it stays the same size and rigid. The timing carries the weight.

## The numbers

Measured from three identical objects dropped about five of their own heights. Rebounds are fractions of the drop; each hop runs from one contact to the next.

| Weight | Fall | Rebound heights (of the drop) | Each hop | Still after first contact |
|---|---|---|---|---|
| light | 0.33s | 0.71, 0.50, 0.19, 0.08 | 0.54, 0.39, 0.27, 0.25s | ~1.45s |
| medium | 0.29s | 0.26, 0.18, 0.07, 0.03 | 0.46, 0.33, 0.22, 0.22s | ~1.25s |
| heavy | 0.21s | 0.044, 0.017, 0.005 | 0.33, 0.25, 0.13s | ~0.7s, then sinks 2% of its height |

What the table says, in words:
- **The fall is the first cue.** The heavy object covers the same drop in two-thirds of the light one's time, so it hits much harder.
- **The first rebound sets the weight**: 71% of the drop is light, a quarter is medium, under 5% is heavy. Each later rebound is roughly half the one before.
- **The little hops stay slow.** A heavy object's 4% rebound still takes a third of a second. Physics would make it a quick buzz; a slow, low hop is what reads as weight. Hops shrink in height much faster than they shrink in time.
- **Falls accelerate all the way down** on \`power2.in\`, with no cushioning before the floor. **Rises decelerate into each apex** on \`power2.out\`. Rise and fall take about the same time.

## Markup

The wrapper moves; the object inside it is whatever is being dropped.

\`\`\`html
<div id="mw-stage" style="position: absolute; inset: 0; display: grid; place-items: center; overflow: hidden;">
  <div id="mw-y" style="grid-area: 1 / 1;">
    <div id="mw-object" style="width: 240px; height: 240px; border-radius: 32px; background: var(--l5-color-0, #101820);"></div>
  </div>
</div>
\`\`\`

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });

const WEIGHTS = {
    light: { fall: 0.33, rebounds: [0.71, 0.5, 0.19, 0.08], hops: [0.54, 0.39, 0.27, 0.25], sink: 0 },
    medium: { fall: 0.29, rebounds: [0.26, 0.18, 0.07, 0.03], hops: [0.46, 0.33, 0.22, 0.22], sink: 0 },
    heavy: { fall: 0.21, rebounds: [0.044, 0.017, 0.005], hops: [0.33, 0.25, 0.13], sink: 0.02 },
};
const MASS = WEIGHTS.heavy;  // a safe, a verdict, a falling price
const LAND = 0.6;            // first contact: the word it lands on
const DROP = 720;            // from above the frame to the resting spot
const SIZE = 240;
const CONTACT = 0.045;       // one frame on the floor, so every contact is seen

gsap.set('#mw-y', { y: -DROP });
tl.to('#mw-y', { y: 0, duration: MASS.fall, ease: 'power2.in' }, LAND - MASS.fall);
let at = LAND;
MASS.rebounds.forEach((rebound, i) => {
    const half = (MASS.hops[i] - CONTACT) / 2;
    tl.to('#mw-y', { y: -rebound * DROP, duration: half, ease: 'power2.out' }, at + CONTACT);
    tl.to('#mw-y', { y: 0, duration: half, ease: 'power2.in' }, at + CONTACT + half);
    at += MASS.hops[i];
});
// The heaviest settles a little into the surface it landed on.
if (MASS.sink) tl.to('#mw-y', { y: MASS.sink * SIZE, duration: 0.12, ease: 'power2.out' }, at);

tl.set({}, {}, at + 0.8);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Choose the weight from the thing.** A safe, an anvil, a building, a verdict, a price crashing: heavy. A box, a phone, a coin, a book: medium. A balloon, a feather, a bubble, a leaf, an idea: light.
- **The beat clock.** Heavy is still about 0.7s after contact, so it fits a one-second beat. Light keeps moving for almost a second and a half: give it the beat that may hold longer, or let the next handoff take it while it is still bouncing (the weight has read by the second hop).
- **Dropping in from out of frame.** The rebounds are fractions of the drop, so a light object dropped from above the frame goes most of the way back up. Drop a light object from inside the frame, or scale its rebounds down together (keeping their ratios) so the first one stays well inside.
- **Sideways travel.** Give the wrapper a constant-speed x as well and the drops become arcs: see \`${SKILL_NAMES.arcPathMotion}\`.
- **With deformation.** A light or medium object that is soft can squash on each contact (\`${SKILL_NAMES.squashAndStretch}\`); a heavy one barely deforms and a rigid one never does.

## Pitfalls

- **Never slow it to fill a scene.** The times in the table are the weight: a slower fall is lighter gravity, not more drama. However long the scene, the drop and its settle take exactly these times and the object then rests. Hops added later to fill the time read as a second drop.
- **The drawn edge touches, not the box.** The element's box has to end where its drawing ends. An SVG whose viewBox leaves room below the shape, or an image with a transparent margin, rests that far above the floor and never touches it, and its scale pivots below the drawing. Crop the viewBox to the shape, or offset the element by the margin, so the drawn bottom is the box's bottom, then look at a still on a contact frame.
- **A contact needs something to land on.** Show the floor: a floor line, the top of another shape, or a contact shadow touching the object's base. A shadow drawn away from the base says the object is floating, and a landing on nothing has no impact.
- **Never shrink the hops evenly in time.** Equal, fast little hops read as vibration, not settling. Keep each hop at least ~0.13s, and keep the low ones slow.
- **Heavy with big rebounds reads as rubber.** If the object is heavy, its first rebound is under a twentieth of the drop.
- **Keep the one-frame contact.** Without it, a render at 30fps can skip the instant the object touches, and it appears to bounce off the air above the floor.
- Don't fade or scale the object as it lands. Weight is told by the motion; anything else changes what is being said.
- The sink is a few pixels. Visible sinking reads as the floor giving way.`,
};
